import { useCallback, useState, useEffect } from 'react';
import { useUnifiedUser } from './useUnifiedUser';
import { useEnhancedChatRealtime } from './useEnhancedChatRealtime';
import { useChatGroupsRealtime } from './useChatGroupsRealtime';
import { useTeamChat } from './useTeamChat';
import { supabase } from '@/integrations/supabase/client';
import { ChatGroup, ChatMessage } from '@/types/chat';
import { toast } from 'sonner';
import { unifiedNotificationService } from '@/services/unifiedNotificationService';
import { unifiedChatService } from '@/services/chat/unifiedChatService';

export const useUnifiedChat = () => {
  const { user } = useUnifiedUser();
  const [chatGroups, setChatGroups] = useState<ChatGroup[]>([]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<ChatGroup | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Team chat functionality
  const { teamChats, loadTeamChats } = useTeamChat();

  console.log('=== UNIFIED CHAT HOOK STATE ===');
  console.log('User:', user?.username, 'Type:', user?.userType);
  console.log('Selected Group:', selectedGroup?.chat_name, selectedGroup?.chat_id);
  console.log('Messages Count:', messages.length);
  console.log('Chat Groups Count:', chatGroups.length);

  // Handle new messages received via real-time
  const handleMessageReceived = useCallback((message: ChatMessage) => {
    console.log('📨 New message received in hook:', message.message_id);
    
    // CRITICAL: Don't add replies to root messages list
    if (message.parent_message_id) {
      console.log('⏭️ Skipping reply message - should be handled by thread component');
      // Dispatch event for thread components to handle
      window.dispatchEvent(new CustomEvent('chat-reply-received', { detail: message }));
      return;
    }
    
    setMessages(prev => {
      // Check if message already exists by ID
      const exists = prev.some(msg => msg.message_id === message.message_id);
      if (exists) {
        console.log('⚠️ Message already exists, skipping duplicate');
        return prev;
      }
      
      // Additional check: Skip if identical message within last 2 seconds
      const duplicateRecent = prev.some(msg => 
        msg.message === message.message &&
        msg.user_id === message.user_id &&
        Math.abs(new Date(msg.created_at).getTime() - new Date(message.created_at).getTime()) < 2000
      );
      
      if (duplicateRecent) {
        console.log('⚠️ Duplicate recent message detected, skipping');
        return prev;
      }
      
      console.log('✅ Adding new message to state');
      return [...prev, message].sort((a, b) => 
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
      );
    });
  }, []);

  // Set up enhanced real-time system
  useEnhancedChatRealtime({
    selectedGroup,
    user,
    onMessageReceived: handleMessageReceived
  });

  // ENHANCED: Load chat groups with better authentication and debugging
  const loadChatGroups = useCallback(async () => {
    if (!user) {
      console.log('⚠️ No user available, skipping chat groups load');
      setLoading(false);
      return;
    }

    try {
      console.log('📋 Loading chat groups for user:', {
        username: user.username,
        id: user.id,
        userType: user.userType
      });
      setLoading(true);
      
      // Use the enhanced service method with current user parameter
      const groups = await unifiedChatService.getUserChatGroups(user);
      
      // Merge regular chat groups with team chats, avoiding duplicates
      const allChats = [...(groups || []), ...teamChats];
      
      // Deduplicate chats based on chat_id to fix duplicate display issue
      const uniqueChats = allChats.reduce((acc, chat) => {
        if (!acc.some(existingChat => existingChat.chat_id === chat.chat_id)) {
          acc.push(chat);
        }
        return acc;
      }, [] as ChatGroup[]);
      
      console.log('✅ Hook received', uniqueChats?.length || 0, 'unique chat groups');
      if (uniqueChats.length > 0) {
        console.log('📝 Group names:', uniqueChats.map(g => g.chat_name));
      }
      
      setChatGroups(uniqueChats || []);
    } catch (error) {
      console.error('❌ Error in loadChatGroups hook:', error);
      setChatGroups([]);
    } finally {
      setLoading(false);
    }
  }, [user, teamChats]);

  // Load messages for a group
  const loadMessages = useCallback(async (groupId: string) => {
    if (!user || !groupId) {
      console.log('Missing user or groupId for message loading');
      return;
    }

    try {
      console.log('💬 Loading messages for group:', groupId);
      
      // Get messages without foreign key join - ONLY root messages (no replies)
      const { data: rawMessages, error } = await supabase
        .from('chat_messages')
        .select('*')
        .eq('chat_group_id', groupId)
        .is('parent_message_id', null)
        .order('created_at', { ascending: true });

      if (error) {
        console.error('💥 Error loading messages:', error);
        setLoading(false);
        return;
      }

      if (!rawMessages || rawMessages.length === 0) {
        console.log('📭 No messages found for group:', groupId);
        setMessages([]);
        setLoading(false);
        return;
      }

      // Get user profiles separately for all message senders
      const userIds = [...new Set(rawMessages.map(msg => msg.user_id).filter(Boolean))];
      
      const [supabaseProfiles, internalUsers] = await Promise.all([
        supabase
          .from('profiles')
          .select('id, username, first_name, last_name, role')
          .in('id', userIds),
        supabase
          .from('internal_users')
          .select('id, username, role')
          .in('id', userIds)
      ]);

      // Enhance messages with user profiles
      const messagesWithProfiles = rawMessages.map(msg => {
        const supabaseProfile = supabaseProfiles.data?.find(p => p.id === msg.user_id);
        const internalProfile = internalUsers.data?.find(u => u.id === msg.user_id);
        
        let userProfile;
        if (supabaseProfile) {
          userProfile = {
            username: supabaseProfile.username || supabaseProfile.first_name || 'User',
            first_name: supabaseProfile.first_name || '',
            last_name: supabaseProfile.last_name || '',
            role: supabaseProfile.role || 'player'
          };
        } else if (internalProfile) {
          userProfile = {
            username: internalProfile.username || 'New School User',
            first_name: internalProfile.username || '',
            last_name: '',
            role: internalProfile.role || 'player'
          };
        } else {
          userProfile = {
            username: `User-${msg.user_id?.slice(0, 8) || 'Unknown'}`,
            first_name: '',
            last_name: '',
            role: 'player'
          };
        }
        
        return {
          ...msg,
          user_profile: userProfile
        };
      });

      console.log('📨 Found', messagesWithProfiles?.length || 0, 'messages');
      console.log('✅ Enhanced messages with profiles successfully');
      setMessages(messagesWithProfiles as ChatMessage[]);
    } catch (error) {
      console.error('❌ Error loading messages:', error);
      setMessages([]);
    }
  }, [user]);

  // Select group and load its messages
  const selectGroup = useCallback(async (group: ChatGroup) => {
    if (!group || !group.chat_id || !user) {
      console.error('Invalid group or user for selection:', { group, user: user?.username });
      return;
    }
    
    console.log('🎯 Selecting group:', group.chat_name, group.chat_id);
    setSelectedGroup(group);
    setMessages([]); // Clear existing messages first
    
    await loadMessages(group.chat_id);
  }, [loadMessages, user]);

  // Send message with TRUE optimistic UI update (show immediately, send in background)
  const sendMessage = useCallback(async (message: string, parentMessageId?: string) => {
    if (!selectedGroup || !user || !message.trim()) {
      console.log('❌ Cannot send message - missing requirements');
      return;
    }

    // Create optimistic message ID for tracking
    const optimisticId = `optimistic-${Date.now()}-${Math.random()}`;
    
    // Create optimistic message object
    const optimisticMessage: ChatMessage = {
      message_id: optimisticId,
      chat_group_id: selectedGroup.chat_id,
      user_id: user.id,
      message: message.trim(),
      created_at: new Date().toISOString(),
      is_read: false,
      parent_message_id: parentMessageId || null,
      user_profile: {
        username: user.username,
        first_name: user.username,
        last_name: '',
        role: 'player'
      }
    };

    try {
      console.log('📤 Sending message with optimistic update:', message);
      console.log('📤 Parent message ID:', parentMessageId || 'none (root message)');
      
      // IMMEDIATE UI UPDATE - Add optimistic message to UI right away (root messages only)
      if (!parentMessageId) {
        setMessages(prev => [...prev, optimisticMessage]);
        console.log('✨ Added optimistic message to UI immediately');
      } else {
        // For replies, dispatch event immediately
        window.dispatchEvent(new CustomEvent('chat-reply-received', { detail: optimisticMessage }));
        console.log('✨ Dispatched optimistic reply event');
      }
      
      // NOW send to server in background
      const newMessage = await unifiedChatService.sendMessage(
        selectedGroup.chat_id,
        message,
        user,
        parentMessageId
      );

      console.log('✅ Message sent via unified service:', newMessage.message_id);

      // Replace optimistic message with real one (root messages only)
      if (!parentMessageId) {
        setMessages(prev => {
          // Remove optimistic message and add real one
          const withoutOptimistic = prev.filter(msg => msg.message_id !== optimisticId);
          const exists = withoutOptimistic.some(msg => msg.message_id === newMessage.message_id);
          if (exists) {
            console.log('Real message already exists, removing optimistic only');
            return withoutOptimistic;
          }
          console.log('Replacing optimistic message with real message');
          return [...withoutOptimistic, newMessage].sort((a, b) => 
            new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
          );
        });
      } else {
        // For replies, dispatch the real message
        window.dispatchEvent(new CustomEvent('chat-reply-received', { detail: newMessage }));
      }

      // Broadcast for cross-tab updates
      console.log('📡 Broadcasting message for cross-tab sync...');
      
      if (user.userType === 'internal') {
        try {
          const existingMessages = JSON.parse(localStorage.getItem('internal_chat_messages') || '[]');
          const updatedMessages = [...existingMessages, newMessage];
          localStorage.setItem('internal_chat_messages', JSON.stringify(updatedMessages));
          console.log('📦 Updated localStorage for cross-tab sync');
        } catch (storageError) {
          console.error('⚠️ Error updating localStorage:', storageError);
        }
      }

      console.log('✅ Message sent successfully');
    } catch (error) {
      console.error('❌ Error sending message:', error);
      
      // Remove optimistic message on error
      if (!parentMessageId) {
        setMessages(prev => prev.filter(msg => msg.message_id !== optimisticId));
        console.log('🗑️ Removed optimistic message due to error');
      }
      
      toast.error('Failed to send message');
    }
  }, [selectedGroup, user]);

  // Create chat group
  const createChatGroup = useCallback(async (chatName: string, selectedUsers: string[] = []) => {
    if (!user) {
      console.log('❌ No user for creating chat group');
      return null;
    }

    try {
      console.log('🆕 Creating chat group:', chatName);
      console.log('📝 Selected users:', selectedUsers);
      
      const { data: group, error: groupError } = await supabase
        .from('chat_groups')
        .insert({
          chat_name: chatName,
          created_by: user.id
        })
        .select()
        .single();

      if (groupError) {
        console.error('❌ Group creation failed:', groupError);
        throw groupError;
      }

      if (!group) {
        throw new Error('No group returned after creation');
      }

      console.log('✅ Chat group created:', group.chat_id);

      // CRITICAL: Add creator to chat_group_users
      const { error: creatorError } = await supabase
        .from('chat_group_users')
        .insert({
          chat_group_id: group.chat_id,
          user_id: user.id,
          is_active: true
        });

      if (creatorError) {
        console.error('❌ Failed to add creator to group:', creatorError);
        throw new Error('Failed to add creator to group: ' + creatorError.message);
      }

      console.log('✅ Creator added to chat_group_users');

      // Add selected users if any
      if (selectedUsers.length > 0) {
        const groupUsers = selectedUsers.map(userId => ({
          chat_group_id: group.chat_id,
          user_id: userId,
          is_active: true
        }));

        const { error: usersError } = await supabase
          .from('chat_group_users')
          .insert(groupUsers);

        if (usersError) {
          console.error('❌ Failed to add some users to group:', usersError);
          // Don't throw - group is created, just log the error
        } else {
          console.log('✅ Added', selectedUsers.length, 'users to group');
        }
      }

      console.log('✅ Chat group created successfully with all members');
      await loadChatGroups();
      return group;
    } catch (error) {
      console.error('❌ Error creating chat group:', error);
      const errorMsg = error instanceof Error ? error.message : 'Unknown error';
      toast.error(`Failed to create chat group: ${errorMsg}`);
      return null;
    }
  }, [user, loadChatGroups]);

  // Set up real-time subscriptions for chat groups
  useChatGroupsRealtime({
    userId: user?.id || null,
    onChatGroupsChange: loadChatGroups
  });

  // Initial load of chat groups
  useEffect(() => {
    loadChatGroups();
  }, [loadChatGroups]);

  // Reload when team chats change
  useEffect(() => {
    if (teamChats.length > 0) {
      loadChatGroups();
    }
  }, [teamChats, loadChatGroups]);

  // Auto-select first group when groups load and no group is selected
  useEffect(() => {
    if (chatGroups.length > 0 && !selectedGroup && !loading && chatGroups[0]) {
      console.log('🎯 Auto-selecting first group:', chatGroups[0]?.chat_name);
      selectGroup(chatGroups[0]);
    }
  }, [chatGroups, selectedGroup, loading, selectGroup]);

  return {
    user,
    chatGroups,
    selectedGroup,
    messages,
    loading,
    selectGroup,
    sendMessage,
    createChatGroup,
    loadChatGroups,
    canCreateGroup: !!user
  };
};
